import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DEAL_STAGES, type DealStage } from '@podcastwiz-app/shared';
import {
  COLUMNS,
  STAGE_LABELS,
  columnTotal,
  money,
  useCampaigns,
  useCompanies,
  useCreateCampaign,
  useDeleteCampaign,
  useCreateCompany,
  useCreateDeal,
  useDeals,
  useMoveDeal,
  useRunAgent,
  useSaveProfile,
  useSponsorProfile,
  type Campaign,
  type Company,
  type Deal,
} from '../lib/sponsors';
import { useJob } from '../lib/transcripts';
import { jobError } from '../lib/jobs';
import { errorInfo } from '../lib/api';
import { Button } from '../components/ui/Button';
import { TextArea, TextField } from '../components/ui/Field';
import { EmptyState, ErrorMessage, Loading, PageHeader } from '../components/ui/Feedback';
import { useToast } from '../components/ui/Toast';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { DealDialog } from '../components/sponsors/DealDialog';
import { Assistant } from '../components/sponsors/Assistant';
import { SponsorSettings } from '../components/sponsors/SponsorSettings';

const TABS = [
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'prospects', label: 'Prospects' },
  { key: 'assistant', label: 'Assistant' },
  { key: 'settings', label: 'Settings' },
] as const;
type Tab = (typeof TABS)[number]['key'];

export function SponsorsPage() {
  const profile = useSponsorProfile();
  if (profile.isLoading) return <Loading />;
  if (profile.isError) return <ErrorMessage error={profile.error} onRetry={() => profile.refetch()} />;
  if (!profile.data) return <Onboarding />;
  return <Workspace podcastName={profile.data.podcastName} />;
}

// ── Onboarding: what the agent needs to pitch the show ──

function Onboarding() {
  const save = useSaveProfile();
  const toast = useToast();
  const [podcastName, setName] = useState('');
  const [podcastDescription, setDescription] = useState('');
  const [niche, setNiche] = useState('');
  const [audience, setAudience] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const audienceSize = Number.parseInt(audience.replace(/[^\d]/g, ''), 10);
    save.mutate(
      {
        podcastName: podcastName.trim(),
        ...(podcastDescription.trim() ? { podcastDescription: podcastDescription.trim() } : {}),
        ...(niche.trim() ? { niche: niche.trim() } : {}),
        ...(Number.isFinite(audienceSize) ? { audienceSize } : {}),
        onboardingCompleted: true,
      },
      { onError: (err) => toast.show(errorInfo(err).message, 'error') },
    );
  };

  return (
    <div className="max-w-xl space-y-6">
      <PageHeader
        title="Sponsors"
        description="Find sponsors, let AI draft the pitch, approve it, and track every deal to signed. First, tell the agent about your show."
      />
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-border p-5">
        <TextField label="Podcast name" value={podcastName} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        <TextArea
          label="What it’s about"
          hint="Sponsors read this. Who listens, and why."
          value={podcastDescription}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField label="Niche" hint="e.g. personal finance, indie games" value={niche} onChange={(e) => setNiche(e.target.value)} maxLength={100} />
        <TextField
          label="Downloads per episode"
          inputMode="numeric"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          hint="Your analytics page shows this."
        />
        <Button type="submit" loading={save.isPending} disabled={!podcastName.trim()}>
          Start finding sponsors
        </Button>
      </form>
    </div>
  );
}

// ── Workspace ──

/** What else goes when a campaign is deleted. */
export function dealCountNote(n: number): string {
  if (n === 0) return 'It has no deals. This can’t be undone.';
  return `Its ${n} deal${n === 1 ? '' : 's'} and their outreach drafts go too. Emails already sent aren’t recalled. This can’t be undone.`;
}

function Workspace({ podcastName }: { podcastName: string }) {
  const campaigns = useCampaigns();
  const deals = useDeals();
  const remove = useDeleteCampaign();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('pipeline');
  const [campaignId, setCampaignId] = useState('');
  const [confirming, setConfirming] = useState(false);

  if (campaigns.isLoading) return <Loading />;
  if (campaigns.isError) return <ErrorMessage error={campaigns.error} onRetry={() => campaigns.refetch()} />;
  const list = campaigns.data ?? [];
  if (list.length === 0) return <FirstCampaign podcastName={podcastName} />;
  const campaign = list.find((c) => c.id === campaignId) ?? list[0]!;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Sponsors"
        description="AI finds sponsors and drafts the pitch; nothing is sent until you approve it."
        actions={
          list.length > 1 ? (
            <select
              aria-label="Campaign"
              value={campaign.id}
              onChange={(e) => setCampaignId(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
            >
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-muted">{campaign.name}</span>
          )
        }
      />
      <div className="-mt-3">
        <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
          Delete campaign
        </Button>
      </div>
      <ConfirmDialog
        open={confirming}
        danger
        title={`Delete “${campaign.name}”?`}
        body={dealCountNote((deals.data ?? []).filter((d) => d.campaignId === campaign.id).length)}
        confirmLabel="Delete"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          remove.mutate(campaign.id, {
            onSuccess: () => {
              setCampaignId('');
              toast.show('Campaign deleted.', 'success');
            },
            onError: (err) => toast.show(errorInfo(err).message, 'error'),
          });
        }}
      />
      <div role="tablist" aria-label="Sponsors" className="inline-flex rounded-md border border-border p-0.5">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`rounded px-3 py-1 text-sm ${tab === t.key ? 'bg-accent-soft font-medium text-accent' : 'text-muted hover:text-text'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'pipeline' && <Pipeline campaign={campaign} onFind={() => setTab('prospects')} />}
      {tab === 'prospects' && <Prospects campaign={campaign} />}
      {tab === 'assistant' && <Assistant campaign={campaign} />}
      {tab === 'settings' && <SponsorSettings />}
    </div>
  );
}

function FirstCampaign({ podcastName }: { podcastName: string }) {
  const create = useCreateCampaign();
  const toast = useToast();
  const [name, setName] = useState(`${podcastName} sponsors`);
  return (
    <div className="max-w-xl space-y-6">
      <PageHeader title="Sponsors" description="A campaign groups the sponsors you’re pitching for a season or a launch." />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ name: name.trim() }, { onError: (err) => toast.show(errorInfo(err).message, 'error') });
        }}
        className="space-y-4 rounded-xl border border-border p-5"
      >
        <TextField label="Campaign name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
          Create campaign
        </Button>
      </form>
    </div>
  );
}

// ── Pipeline board ──

function Pipeline({ campaign, onFind }: { campaign: Campaign; onFind: () => void }) {
  const deals = useDeals();
  const companies = useCompanies();
  const move = useMoveDeal();
  const toast = useToast();
  const [open, setOpen] = useState<Deal | null>(null);

  if (deals.isLoading || companies.isLoading) return <Loading />;
  if (deals.isError) return <ErrorMessage error={deals.error} onRetry={() => deals.refetch()} />;
  const byId = new Map((companies.data ?? []).map((c) => [c.id, c]));
  const mine = (deals.data ?? []).filter((d) => d.campaignId === campaign.id);

  if (mine.length === 0) {
    return (
      <EmptyState
        title="No deals yet"
        body="Find sponsors that fit your show, then add them to the pipeline."
        action={<Button onClick={onFind}>Find sponsors</Button>}
      />
    );
  }

  const onMove = (deal: Deal, stage: DealStage) =>
    move.mutate({ id: deal.id, stage }, { onError: (err) => toast.show(errorInfo(err).message, 'error') });

  return (
    <>
      <div className="grid gap-3 overflow-x-auto pb-2 md:grid-cols-5">
        {COLUMNS.map((col) => {
          const cards = mine.filter((d) => col.stages.includes(d.stage));
          const total = columnTotal(cards);
          return (
            <section key={col.key} aria-label={col.label} className="min-w-52 space-y-2 rounded-xl bg-surface p-2">
              <h2 className="flex items-baseline justify-between px-1 text-sm font-semibold">
                <span>
                  {col.label} <span className="font-normal text-muted">{cards.length}</span>
                </span>
                {total && <span className="text-xs font-normal text-muted">{total}</span>}
              </h2>
              {cards.map((d) => (
                <DealCard key={d.id} deal={d} company={byId.get(d.companyId)} onOpen={() => setOpen(d)} onMove={(s) => onMove(d, s)} />
              ))}
            </section>
          );
        })}
      </div>
      <DealDialog deal={open} company={open ? byId.get(open.companyId) : undefined} onClose={() => setOpen(null)} />
    </>
  );
}

function DealCard({
  deal,
  company,
  onOpen,
  onMove,
}: {
  deal: Deal;
  company: Company | undefined;
  onOpen: () => void;
  onMove: (stage: DealStage) => void;
}) {
  const name = company?.name ?? 'Unknown sponsor';
  return (
    <article className="space-y-2 rounded-lg border border-border bg-bg p-3 text-sm shadow-sm">
      <button type="button" onClick={onOpen} className="block w-full text-left font-medium hover:text-accent">
        {name}
      </button>
      {deal.dealValueCents !== null && <div className="text-xs text-muted">{money(deal.dealValueCents, deal.currency)}</div>}
      <select
        aria-label={`Stage for ${name}`}
        value={deal.stage}
        onChange={(e) => onMove(e.target.value as DealStage)}
        className="w-full rounded border border-border bg-bg px-2 py-1 text-xs"
      >
        {DEAL_STAGES.map((s) => (
          <option key={s} value={s}>
            {STAGE_LABELS[s]}
          </option>
        ))}
      </select>
    </article>
  );
}

// ── Prospects: AI discovery + manual adds ──

function Prospects({ campaign }: { campaign: Campaign }) {
  const companies = useCompanies();
  const deals = useDeals();
  const createDeal = useCreateDeal();
  const agent = useRunAgent();
  const toast = useToast();
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useJob(jobId);
  const status = job.data?.status;

  useEffect(() => {
    if (status === 'completed') {
      void qc.invalidateQueries({ queryKey: ['companies'] });
      toast.show('New sponsor ideas are in.', 'success');
      setJobId(null);
    }
  }, [status, qc, toast]);

  const inPipeline = new Set((deals.data ?? []).filter((d) => d.campaignId === campaign.id).map((d) => d.companyId));
  const list = [...(companies.data ?? [])].sort((a, b) => (b.aiRelevanceScore ?? -1) - (a.aiRelevanceScore ?? -1));
  const searching = !!jobId && status !== 'failed';

  const discover = () =>
    agent.mutate(
      { type: 'discover', campaignId: campaign.id },
      {
        onSuccess: (r) => setJobId(r.jobId),
        onError: (err) => toast.show(errorInfo(err).message, 'error'),
      },
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">Find sponsors with AI</h2>
          <p className="text-sm text-muted">Researches brands that fit your show and audience, and scores how good a fit each is.</p>
        </div>
        <Button onClick={discover} loading={agent.isPending || searching}>
          {searching ? 'Searching…' : 'Find sponsors'}
        </Button>
        {status === 'failed' && (
          <p className="w-full text-sm text-danger" title={job.data?.errorMessage ?? undefined}>
            {jobError(job.data?.errorMessage ?? null)}
          </p>
        )}
      </div>

      <AddCompany />

      {companies.isLoading && <Loading />}
      {companies.isError && <ErrorMessage error={companies.error} onRetry={() => companies.refetch()} />}
      {companies.data && list.length === 0 && <p className="text-sm text-muted">No prospects yet.</p>}
      {list.length > 0 && (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {list.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium">
                  {c.name}
                  {c.domain && <span className="ml-2 text-xs font-normal text-muted">{c.domain}</span>}
                </div>
                {(c.industry || c.description) && (
                  <p className="line-clamp-2 text-sm text-muted">{[c.industry, c.description].filter(Boolean).join(' — ')}</p>
                )}
              </div>
              {c.aiRelevanceScore !== null && (
                <span className="text-xs text-muted" title="How well the AI thinks this brand fits your show">
                  Fit {c.aiRelevanceScore}/100
                </span>
              )}
              {inPipeline.has(c.id) ? (
                <span className="text-xs text-success">✓ In pipeline</span>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={createDeal.isPending && createDeal.variables?.companyId === c.id}
                  onClick={() =>
                    createDeal.mutate(
                      { campaignId: campaign.id, companyId: c.id },
                      {
                        onSuccess: () => toast.show(`${c.name} added to the pipeline`, 'success'),
                        onError: (err) => toast.show(errorInfo(err).message, 'error'),
                      },
                    )
                  }
                >
                  Add to pipeline
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AddCompany() {
  const create = useCreateCompany();
  const toast = useToast();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { name: name.trim(), ...(domain.trim() ? { domain: domain.trim() } : {}) },
          {
            onSuccess: () => {
              setName('');
              setDomain('');
            },
            onError: (err) => toast.show(errorInfo(err).message, 'error'),
          },
        );
      }}
      className="flex flex-wrap items-end gap-2"
    >
      <TextField label="Add a sponsor yourself" placeholder="Brand name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      <TextField label="Website" placeholder="brand.com" value={domain} onChange={(e) => setDomain(e.target.value)} maxLength={255} />
      <Button type="submit" variant="secondary" loading={create.isPending} disabled={!name.trim()}>
        Add
      </Button>
    </form>
  );
}
